// src/utils/http.js
import router from '@/router'
import { Message } from 'element-ui'

const BASE_URL = ''

const PARAM_TYPE_MSG = '参数值不能是字符类型'

function looksLikeParamTypeError(msg) {
  if (!msg || typeof msg !== 'string') return false
  return /Cannot deserialize|not a valid|Failed to convert|NumberFormatException|parse error|HttpMessageNotReadable|类型不匹配/i.test(msg)
}

/** 接口失败时给用户看的文案（技术异常转成人话） */
export function getErrorMessage(error, fallback = '操作失败') {
  if (error == null || error === '') return fallback
  let raw = ''
  if (typeof error === 'string') {
    raw = error
  } else if (typeof error === 'object') {
    raw = error.msg || error.message || ''
    if (!raw && error.data && typeof error.data === 'string') raw = error.data
  }
  if (looksLikeParamTypeError(raw)) return PARAM_TYPE_MSG
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  return fallback
}

async function request(url, options = {}) {
  const token = localStorage.getItem('token')
  
  // 去掉 /api 前缀
  const cleanUrl = url.replace(/^\/api/, '')

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { token })
  }

  const response = await fetch(`${BASE_URL}${cleanUrl}`, {
    headers,
    ...options
  })

  if (response.status === 401) {
    localStorage.removeItem('token')
    localStorage.removeItem('role')
    localStorage.removeItem('status')
    localStorage.removeItem('username')
    Message.error('登录已过期，请重新登录')
    router.push('/login')
    return { code: 401, msg: '登录已过期' }
  }

  let result
  try {
    result = await response.json()
  } catch (e) {
    throw new Error(fallbackHttpError(response.status))
  }

  if (result.code === 200 || result.code === 1) {
    return result
  } else {
    throw new Error(getErrorMessage(result, `请求失败`))
  }
}

function fallbackHttpError(status) {
  if (status >= 500) return '服务暂时不可用，请稍后重试'
  if (status === 400) return '请求参数有误'
  return '请求失败'
}

/** 从接口响应中提取可展示的成功/提示文案（忽略对象类型的 data） */
export function getResponseMessage(result, fallback = '') {
  if (!result) return fallback
  const msg = result.msg ?? result.message
  if (typeof msg === 'string' && msg.trim()) {
    if (looksLikeParamTypeError(msg)) return PARAM_TYPE_MSG
    return msg.trim()
  }
  if (typeof result.data === 'string' && result.data.trim()) return result.data.trim()
  return fallback
}

export async function get(url, params = {}) {
  const queryString = new URLSearchParams(params).toString()
  const fullUrl = queryString ? `${url}?${queryString}` : url
  return request(fullUrl, { method: 'GET' })
}

export async function post(url, data = {}, params = {}) {
  const queryString = new URLSearchParams(params).toString()
  const fullUrl = queryString ? `${url}?${queryString}` : url
  return request(fullUrl, {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

export async function put(url, data = {}) {
  return request(url, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

export async function del(url) {
  return request(url, { method: 'DELETE' })
}

// 文件上传
export async function upload(file, path = '') {
  const token = localStorage.getItem('token')
  const formData = new FormData()
  formData.append('image', file)
  if (path) formData.append('path', path)

  const response = await fetch(`${BASE_URL}/common/upload`, {
    method: 'POST',
    headers: { ...(token && { token }) },
    body: formData
  })

  const result = await response.json()
  if (result.code === 200 || result.code === 1) {
    return result
  } else {
    throw new Error(result.msg || '上传失败')
  }
}

export { BASE_URL }
export { request }

/**
 * 通用文件上传(对接后端 /api/admin/file/upload 接口,使用阿里云OSS)
 * @param {File} file - 要上传的文件
 * @returns {Promise<string>} 返回文件访问URL路径(OSS完整URL)
 */
export async function uploadFile(file) {
  const token = localStorage.getItem('token')

  if (!file || !file.size) {
    throw new Error('请选择文件')
  }

  const formData = new FormData()
  formData.append('image', file)

  const headers = {}
  if (token) {
    headers.token = token
  }

  const response = await fetch(`${BASE_URL}/admin/file/upload`, {
    method: 'POST',
    headers,
    body: formData
  })

  if (response.status === 401) {
    localStorage.removeItem('token')
    localStorage.removeItem('role')
    localStorage.removeItem('status')
    localStorage.removeItem('username')
    Message.warning('登录已过期，请重新登录')
    router.push('/login')
    throw new Error('登录已过期')
  }

  const result = await response.json()
  if (result.code === 200 || result.code === 1) {
    return result.data
  }
  throw new Error(result.msg || '上传失败')
}

// 图片资源基础路径
export const IMG_BASE_URL = ''